import { borrowMarketsQueryParams } from '@/config/query/queryParams'
import {
  BORROW_LIQUIDITY_FROM_BOUND_KEY,
  BORROW_LIQUIDITY_TO_BOUND_KEY,
  BORROW_MARKET_SIZE_FROM_BOUND_KEY,
  BORROW_MARKET_SIZE_TO_BOUND_KEY,
  PAGE_KEY,
  SEARCH_FOR_KEY,
  SELECTED_COLLATERALS_KEY,
  SELECTED_NETWORKS_KEY,
  SELECTED_PROTOCOLS_KEY,
  SELECTED_TOKENS_KEY,
} from '@/constants/state'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { filterNetworks } from '@vaultsfyi/common/utils'
import { useSearchState } from './useSearchState'

export function useBorrowMarketsSearchState() {
  const { searchState, touchedParamsCount, updateSearchState, resetSearchParam, clearSearchState } = useSearchState({
    debounceTimeout: SECOND_IN_MILLISECONDS / 2,
    debouncedKeys: [
      SEARCH_FOR_KEY,
      BORROW_MARKET_SIZE_FROM_BOUND_KEY,
      BORROW_MARKET_SIZE_TO_BOUND_KEY,
      BORROW_LIQUIDITY_FROM_BOUND_KEY,
      BORROW_LIQUIDITY_TO_BOUND_KEY,
      SELECTED_NETWORKS_KEY,
      SELECTED_PROTOCOLS_KEY,
      SELECTED_TOKENS_KEY,
      SELECTED_COLLATERALS_KEY,
    ],
  })

  const pageParam = Number(searchState.get(PAGE_KEY))
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1
  const searchFor = searchState.get(SEARCH_FOR_KEY) ?? ''
  const selectedNetworks = filterNetworks(searchState.getAll(SELECTED_NETWORKS_KEY))
  const selectedProtocols = searchState.getAll(SELECTED_PROTOCOLS_KEY)
  const selectedTokens = searchState.getAll(SELECTED_TOKENS_KEY)
  const selectedCollaterals = searchState.getAll(SELECTED_COLLATERALS_KEY)
  const selectedMarketSizeRange = getNumberRangeSearchState({
    searchState,
    fromKey: BORROW_MARKET_SIZE_FROM_BOUND_KEY,
    toKey: BORROW_MARKET_SIZE_TO_BOUND_KEY,
    defaultFrom: borrowMarketsQueryParams.minTvl,
  })
  const selectedLiquidityRange = getNumberRangeSearchState({
    searchState,
    fromKey: BORROW_LIQUIDITY_FROM_BOUND_KEY,
    toKey: BORROW_LIQUIDITY_TO_BOUND_KEY,
  })

  return {
    searchState,
    touchedParamsCount,
    updateSearchState,
    clearSearchState,
    resetSearchParam,
    page,
    searchFor,
    selectedNetworks,
    selectedProtocols,
    selectedTokens,
    selectedCollaterals,
    selectedMarketSizeRange,
    selectedLiquidityRange,
  } as const
}

export interface NumberRangeSearchState {
  from?: number
  to?: number
}

interface GetNumberRangeSearchStateProps {
  searchState: URLSearchParams
  fromKey: string
  toKey: string
  defaultFrom?: number
  defaultTo?: number
}

const getNumberRangeSearchState = ({
  searchState,
  fromKey,
  toKey,
  defaultFrom,
  defaultTo,
}: GetNumberRangeSearchStateProps): NumberRangeSearchState => {
  const from = getNonNegativeNumberSearchParam(searchState, fromKey) ?? defaultFrom
  const to = getNonNegativeNumberSearchParam(searchState, toKey) ?? defaultTo

  if (from !== undefined && to !== undefined && from > to) {
    return { from: to, to: from }
  }

  return { from, to }
}

const getNonNegativeNumberSearchParam = (searchState: URLSearchParams, key: string) => {
  const rawValue = searchState.get(key)

  if (rawValue === null) {
    return undefined
  }

  const value = Number(rawValue)

  if (!Number.isFinite(value) || value < 0) {
    return undefined
  }

  return value
}
